<!-- Liste -->
<div *ngIf="!order" class="card">
  <div class="flex flex-wrap items-center justify-between gap-3 px-4 pt-4">
    <div>
      <h2 class="text-base font-medium m-0">Commandes</h2>
      <p class="text-secondary text-xs m-0">Commandes adressées à ce fournisseur.</p>
    </div>
    <mat-button-toggle-group [value]="status" (change)="onStatusChange($event.value)">
      <mat-button-toggle *ngFor="let statusFilter of statusFilters" [value]="statusFilter.value">
        {{ statusFilter.label }}
      </mat-button-toggle>
    </mat-button-toggle-group>
  </div>

  <mat-progress-bar *ngIf="loading" mode="indeterminate" class="mt-3"></mat-progress-bar>
  <div *ngIf="errorMessage" class="px-4 pt-3">
    <base-banner-message [text]="errorMessage" alertType="alert" [noMargin]="true"></base-banner-message>
  </div>

  <div *ngIf="items.length" class="api-table-wrapper mt-3">
    <table class="api-table">
      <thead>
        <tr>
          <th>N°</th>
          <th>Date</th>
          <th>Établissement</th>
          <th class="num">Lignes</th>
          <th class="num">Total HT</th>
          <th class="num">Total TTC</th>
          <th>Statut</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let item of items" class="clickable" (click)="openOrder(item)">
          <td class="font-medium">{{ item.technicalIdentifier }}</td>
          <td>{{ item.orderDate | date: 'dd/MM/yyyy' }}</td>
          <td>{{ item.establishment?.label }}</td>
          <td class="num">{{ item.numberOfLines }}</td>
          <td class="num">{{ item.totalAmountExcludingTax | currency: 'EUR' }}</td>
          <td class="num">{{ item.totalAmountIncludingTax | currency: 'EUR' }}</td>
          <td>
            <vex-api-tester-status [status]="item.apiStatus" [labels]="ORDER_STATUS_LABELS"></vex-api-tester-status>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
  <div *ngIf="!loading && !errorMessage && !items.length" class="empty-state">Aucune commande pour ce filtre.</div>
  <mat-paginator
    *ngIf="pagination?.total"
    [length]="pagination.total"
    [pageIndex]="pageIndex"
    [pageSize]="pageSize"
    [pageSizeOptions]="[10, 25, 50]"
    (page)="onPage($event)"
  ></mat-paginator>
</div>

<!-- Fiche commande -->
<ng-container *ngIf="order && !billable">
  <div class="breadcrumb mb-3">
    <a (click)="backToList()">Commandes</a>
    ›
    <strong>{{ order.technicalIdentifier }}</strong>
  </div>

  <div class="card p-4">
    <div class="flex flex-wrap items-center gap-3 mb-4">
      <h2 class="text-lg font-medium m-0">{{ order.technicalIdentifier }}</h2>
      <vex-api-tester-status [status]="order.apiStatus" [labels]="ORDER_STATUS_LABELS"></vex-api-tester-status>
      <span class="flex-auto"></span>
      <button
        mat-stroked-button
        color="warn"
        type="button"
        [disabled]="!canDecideOrder()"
        (click)="decideOrder(true)"
        matTooltip="POST /supplier/v1/orders/:orderId/confirm"
      >
        Refuser la commande
      </button>
      <button
        mat-raised-button
        color="primary"
        type="button"
        [disabled]="!canDecideOrder()"
        (click)="decideOrder(false)"
        matTooltip="POST /supplier/v1/orders/:orderId/confirm"
      >
        Confirmer la commande
      </button>
    </div>

    <dl class="fields">
      <div>
        <dt>Date de commande</dt>
        <dd>{{ order.orderDate | date: 'dd/MM/yyyy' }}</dd>
      </div>
      <div>
        <dt>Date d'autorisation</dt>
        <dd>{{ (order.authorizationDate | date: 'dd/MM/yyyy') || '-' }}</dd>
      </div>
      <div>
        <dt>Établissement</dt>
        <dd>{{ order.establishment?.label || '-' }}</dd>
      </div>
      <div>
        <dt>SIRET</dt>
        <dd>{{ order.establishment?.siret || '-' }}</dd>
      </div>
      <div>
        <dt>N° client</dt>
        <dd>{{ order.establishment?.customerNumber || '-' }}</dd>
      </div>
      <div>
        <dt>Contact commande</dt>
        <dd>{{ formatContact(order.purchaseOrderContact) || '-' }}</dd>
      </div>
      <div>
        <dt>Adresse de livraison</dt>
        <dd>{{ formatAddress(order.deliveryAddress) || '-' }}</dd>
      </div>
      <div>
        <dt>Adresse de facturation</dt>
        <dd>{{ formatAddress(order.billingAddress) || '-' }}</dd>
      </div>
      <div>
        <dt>Total HT</dt>
        <dd>{{ order.totalAmountExcludingTax | currency: 'EUR' }}</dd>
      </div>
      <div>
        <dt>TVA</dt>
        <dd>{{ order.vatAmount | currency: 'EUR' }}</dd>
      </div>
      <div>
        <dt>Total TTC</dt>
        <dd>{{ order.totalAmountIncludingTax | currency: 'EUR' }}</dd>
      </div>
    </dl>
    <p *ngIf="!canDecideOrder()" class="text-secondary text-xs mt-3 mb-0">
      Une commande ne se confirme ou ne se refuse qu'une fois, tant qu'elle est « Nouvelle ».
    </p>
  </div>

  <mat-progress-bar *ngIf="detailLoading" mode="indeterminate" class="mt-4"></mat-progress-bar>
  <div *ngIf="detailError" class="mt-4">
    <base-banner-message [text]="detailError" alertType="alert" [noMargin]="true"></base-banner-message>
  </div>

  <div class="card mt-4">
    <div class="px-4 pt-4">
      <h3 class="text-base font-medium m-0">Lignes de la commande</h3>
    </div>
    <div *ngIf="orderLines.length" class="api-table-wrapper mt-3">
      <table class="api-table">
        <thead>
          <tr>
            <th>Libellé</th>
            <th>Réf. fournisseur</th>
            <th>Type</th>
            <th class="num">Quantité</th>
            <th class="num">Prix HT</th>
            <th class="num">Total TTC</th>
            <th class="num">Livré</th>
            <th class="num">Reçu</th>
            <th class="num">Reste</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let line of orderLines">
            <td>{{ line.label || '-' }}</td>
            <td>{{ line.supplierReference || '-' }}</td>
            <td>{{ LINE_TYPE_LABELS[line.lineType] ?? line.lineType }}</td>
            <td class="num">{{ line.quantity?.value }} {{ line.quantity?.unit }}</td>
            <td class="num">{{ line.priceExcludingTax | currency: 'EUR' }}</td>
            <td class="num">{{ line.totalAmountIncludingTax | currency: 'EUR' }}</td>
            <td class="num">{{ line.deliveryInformation?.deliveredQuantity }}</td>
            <td class="num">{{ line.deliveryInformation?.receivedQuantity }}</td>
            <td class="num">{{ line.deliveryInformation?.remainingQuantity }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div *ngIf="!detailLoading && !orderLines.length" class="empty-state">Aucune ligne.</div>
  </div>

  <div class="card mt-4">
    <div class="px-4 pt-4">
      <h3 class="text-base font-medium m-0">Facturables</h3>
      <p class="text-secondary text-xs m-0">Réceptions de la commande, à facturer par le fournisseur.</p>
    </div>
    <div *ngIf="billables.length" class="api-table-wrapper mt-3">
      <table class="api-table">
        <thead>
          <tr>
            <th>Réception</th>
            <th>Date</th>
            <th>Statut de réception</th>
            <th class="num">Lignes</th>
            <th class="num">Total HT</th>
            <th class="num">Total TTC</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let item of billables" class="clickable" (click)="openBillable(item)">
            <td class="font-medium">{{ item.reception?.technicalIdentifier }}</td>
            <td>{{ item.reception?.receptionDate | date: 'dd/MM/yyyy' }}</td>
            <td>{{ item.reception?.receptionStatus }}</td>
            <td class="num">{{ item.numberOfLines }}</td>
            <td class="num">{{ item.totalAmountExcludingTax | currency: 'EUR' }}</td>
            <td class="num">{{ item.totalAmountIncludingTax | currency: 'EUR' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div *ngIf="!detailLoading && !billables.length" class="empty-state">Aucune réception sur cette commande.</div>
  </div>
</ng-container>

<!-- Fiche facturable -->
<ng-container *ngIf="billable">
  <div class="breadcrumb mb-3">
    <a (click)="backToList()">Commandes</a>
    ›
    <a (click)="backToOrder()">{{ order.technicalIdentifier }}</a>
    ›
    <strong>{{ billable.reception?.technicalIdentifier }}</strong>
  </div>

  <div class="card p-4">
    <h2 class="text-lg font-medium m-0 mb-4">Facturable {{ billable.reception?.technicalIdentifier }}</h2>
    <dl class="fields">
      <div>
        <dt>Date de réception</dt>
        <dd>{{ (billable.reception?.receptionDate | date: 'dd/MM/yyyy') || '-' }}</dd>
      </div>
      <div>
        <dt>Statut de réception</dt>
        <dd>{{ billable.reception?.receptionStatus || '-' }}</dd>
      </div>
      <div>
        <dt>Établissement</dt>
        <dd>{{ billable.establishment?.label || '-' }}</dd>
      </div>
      <div>
        <dt>Contact commande</dt>
        <dd>{{ formatContact(billable.purchaseOrderContact) || '-' }}</dd>
      </div>
      <div>
        <dt>Contact facturation</dt>
        <dd>{{ formatContact(billable.billingContact) || '-' }}</dd>
      </div>
      <div>
        <dt>Adresse de facturation</dt>
        <dd>{{ formatAddress(billable.billingAddress) || '-' }}</dd>
      </div>
      <div>
        <dt>Échéance de paiement</dt>
        <dd>{{ billable.paymentDeadline || '-' }}</dd>
      </div>
      <div>
        <dt>Total HT</dt>
        <dd>{{ billable.totalAmountExcludingTax | currency: 'EUR' }}</dd>
      </div>
      <div>
        <dt>TVA</dt>
        <dd>{{ billable.vatAmount | currency: 'EUR' }}</dd>
      </div>
      <div>
        <dt>Total TTC</dt>
        <dd>{{ billable.totalAmountIncludingTax | currency: 'EUR' }}</dd>
      </div>
    </dl>
  </div>

  <mat-progress-bar *ngIf="billableLoading" mode="indeterminate" class="mt-4"></mat-progress-bar>
  <div *ngIf="billableError" class="mt-4">
    <base-banner-message [text]="billableError" alertType="alert" [noMargin]="true"></base-banner-message>
  </div>

  <div class="card mt-4">
    <div class="px-4 pt-4">
      <h3 class="text-base font-medium m-0">Lignes facturables</h3>
    </div>
    <div *ngIf="billableLines.length" class="api-table-wrapper mt-3">
      <table class="api-table">
        <thead>
          <tr>
            <th>Libellé</th>
            <th>Réf. fournisseur</th>
            <th class="num">Quantité</th>
            <th class="num">Prix HT</th>
            <th class="num">TVA</th>
            <th class="num">Total TTC</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let line of billableLines">
            <td>{{ line.label || '-' }}</td>
            <td>{{ line.supplierReference || '-' }}</td>
            <td class="num">{{ line.quantity?.value }} {{ line.quantity?.unit }}</td>
            <td class="num">{{ line.priceExcludingTax | currency: 'EUR' }}</td>
            <td class="num">{{ line.vatAmount | currency: 'EUR' }}</td>
            <td class="num">{{ line.totalAmountIncludingTax | currency: 'EUR' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div *ngIf="!billableLoading && !billableLines.length" class="empty-state">Aucune ligne.</div>
  </div>
</ng-container>
